import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FileText, MessageSquare, Pencil, ExternalLink, Briefcase, ArrowRight,
  Mail, ChevronDown, ChevronUp, Clock, Check, Link as LinkIcon, Archive, RotateCcw,
  ScanSearch,
} from "lucide-react";
import { toast } from "sonner";
import Shell from "@/components/Shell";
import { Card, PageHeader, Spinner, Empty, StatusBadge, Btn } from "@/components/ui-kit";
import RecipientMultiSelectPopover from "@/components/RecipientMultiSelectPopover";
import VisualCheckDialog from "@/components/VisualCheckDialog";
import { formatDateUK } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

// Build a human-readable slug for a proof share URL. Mirrors logic in
// ProjectProofs.jsx so links generated here match what the user sees there.
const buildProofSlug = (proof, projectName) => {
  const slugify = (s) => (s || "")
    .toLowerCase()
    .replace(/['"`]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const base = proof.name || projectName || "proof";
  const baseSlug = slugify(base);
  const projectSlug = projectName ? slugify(projectName) : "";
  const withProject =
    proof.name && projectSlug && !baseSlug.includes(projectSlug)
      ? `${projectName} ${base}`
      : base;
  const versionSuffix = proof.version ? `v${proof.version}` : "";
  const hasVersion = versionSuffix && new RegExp(`v${proof.version}\\b`, "i").test(withProject);
  const full = slugify(withProject + (versionSuffix && !hasVersion ? ` ${versionSuffix}` : ""));
  if (full.length <= 80) return full;
  const trailingVersion = full.match(/-v\d+$/);
  if (trailingVersion) {
    const head = full.slice(0, 80 - trailingVersion[0].length).replace(/-+$/, "");
    return head + trailingVersion[0];
  }
  return full.slice(0, 80).replace(/-+$/, "");
};

const Proofing = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [vcOpen, setVcOpen] = useState(false);
  const { user } = useAuth();

  // Email history & recipients state — keyed by proof id / client id.
  const [emailLogs, setEmailLogs] = useState({});    // { proofId: [entries] }
  const [emailLogOpen, setEmailLogOpen] = useState({}); // { proofId: bool }
  const [contactsByClient, setContactsByClient] = useState({}); // { clientId: [{name,email}] }
  const [copiedId, setCopiedId] = useState(null);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get(`/proofs`, { params: { live_only: !showAll } });
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAll]);

  // Active vs Completed split (manual + auto rule):
  //   • completion_override = "completed" → forced into Completed bucket
  //   • completion_override = "active"    → forced into Active bucket
  //   • otherwise auto-rule: status === "Pending Review" OR unresolved > 0 → Active.
  //     Approved or all-resolved Changes-Requested fall through to Completed.
  const proofUnresolvedCount = (p) => {
    const commentUnresolved = (p.comments || []).filter((c) => !c.resolved).length;
    const drawingUnresolved = (p.annotations || []).filter((a) => !a.resolved).length;
    return commentUnresolved + drawingUnresolved;
  };

  const proofBucket = (p) => {
    if (p.completion_override === "completed") return "completed";
    if (p.completion_override === "active") return "active";
    const status = (p.status || "").toLowerCase();
    if (status === "approved") return "completed";
    if (status === "pending review" || proofUnresolvedCount(p) > 0) return "active";
    return "completed";
  };

  const grouped = useMemo(() => {
    const map = {};
    items.forEach((p) => {
      const key = p.project_id;
      if (!map[key]) {
        map[key] = {
          project_id: key, project_name: p.project_name, job_number: p.job_number,
          project_status: p.project_status, client_name: p.client_name, client_id: p.client_id,
          proofs: [],
        };
      }
      map[key].proofs.push(p);
    });

    const groups = Object.values(map).map((g) => {
      // Sort newest first by upload date — version number alone misleads when
      // the user uploads several different proofs (each with its own v1).
      const all = [...g.proofs].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      const active = [];
      const completed = [];
      all.forEach((p) => {
        if (proofBucket(p) === "active") active.push(p);
        else completed.push(p);
      });
      // For sorting groups: prefer projects with the most-recent activity.
      const latestActivity = all[0]?.created_at;
      return { ...g, active, completed, latestActivity };
    });

    return groups.sort(
      (a, b) => new Date(b.latestActivity) - new Date(a.latestActivity)
    );
  }, [items]);

  // Optimistically flip a proof's bucket and persist the override server-side.
  const setProofCompletion = async (proof, state) => {
    // Optimistic update so the UI snaps immediately.
    setItems((prev) => prev.map((x) => x.id === proof.id ? { ...x, completion_override: state } : x));
    try {
      await api.post(`/proofs/${proof.id}/completion`, { state });
    } catch (err) {
      // Roll back on failure.
      setItems((prev) => prev.map((x) => x.id === proof.id ? { ...x, completion_override: proof.completion_override ?? null } : x));
      throw err;
    }
  };

  // Lazily fetch a client's contacts so we can populate the "Email link"
  // recipient picker. Cached per client_id.
  const ensureContacts = async (clientId) => {
    if (!clientId) return [];
    if (contactsByClient[clientId]) return contactsByClient[clientId];
    try {
      const { data } = await api.get(`/clients/${clientId}`);
      const out = [];
      (data.contacts || []).forEach((c) => {
        const e = (c.email || "").trim();
        if (e) out.push({ name: c.name || e, email: e });
      });
      const ce = (data.email || "").trim();
      if (ce && !out.some((x) => x.email.toLowerCase() === ce.toLowerCase())) {
        out.unshift({ name: data.name || ce, email: ce });
      }
      setContactsByClient((prev) => ({ ...prev, [clientId]: out }));
      return out;
    } catch {
      setContactsByClient((prev) => ({ ...prev, [clientId]: [] }));
      return [];
    }
  };

  const loadEmailLog = async (proofId) => {
    try {
      const { data } = await api.get(`/proofs/${proofId}`);
      setEmailLogs((prev) => ({ ...prev, [proofId]: data.email_log || [] }));
    } catch { /* ignore */ }
  };
  const toggleEmailLog = (proofId) => {
    const willOpen = !emailLogOpen[proofId];
    setEmailLogOpen((prev) => ({ ...prev, [proofId]: willOpen }));
    if (willOpen && !(proofId in emailLogs)) loadEmailLog(proofId);
  };

  // Build a mailto: URL targeting one or more recipients. The multi-recipient
  // case comma-joins emails in the To: field so a single draft is sent rather
  // than firing N separate mailtos. Greeting adapts to recipient count.
  const buildProofMailto = (proof, projectName, recipients = []) => {
    const slug = buildProofSlug(proof, projectName);
    const url = `${window.location.origin}/proof/${proof.share_token}${slug ? `/${slug}` : ""}`;
    const list = (recipients || []).filter((r) => r?.email);
    let greeting = "Hi there";
    if (list.length === 1) {
      greeting = `Hi ${list[0].name ? list[0].name.split(/\s+/)[0] : "there"}`;
    } else if (list.length > 1) {
      greeting = "Hi all";
    }
    const subject = `Proof ready for review — ${proof.name}${proof.version > 1 ? ` (v${proof.version})` : ""}`;
    const body = [
      `${greeting},`,
      "",
      `Your latest proof for ${projectName || "the project"} is ready to review. You can leave notes or sketch directly on the PDF, then approve or request changes when you're done — no login required.`,
      "",
      `View the proof: ${url}`,
      "",
      "Let me know if you have any questions.",
    ].join("\n");
    const to = list.length
      ? encodeURIComponent(list.map((r) => r.email).join(", "))
      : "";
    return `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const openMail = (proof, projectName, recipients = []) => {
    const list = (recipients || []).filter((r) => r?.email || r?.name);
    const writes = list.length
      ? list.map((r) =>
          api.post(`/proofs/${proof.id}/email-log`, {
            recipient_name: r?.name || "",
            recipient_email: r?.email || "",
          })
        )
      : [
          api.post(`/proofs/${proof.id}/email-log`, { recipient_name: "", recipient_email: "" }),
        ];
    Promise.allSettled(writes).then(() => loadEmailLog(proof.id));
    window.location.href = buildProofMailto(proof, projectName, list);
  };

  const copyShareLink = async (proof, projectName) => {
    const slug = buildProofSlug(proof, projectName);
    const url = `${window.location.origin}/proof/${proof.share_token}${slug ? `/${slug}` : ""}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      ta.remove();
    }
    setCopiedId(proof.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // Per-project state for the "Completed Proofs" collapsible panel. Defaults
  // closed so the main panel stays clean — the user only expands when they
  // need to dip into history.
  const [completedOpen, setCompletedOpen] = useState({});
  const toggleCompleted = (projectId) => {
    setCompletedOpen((prev) => ({ ...prev, [projectId]: !prev[projectId] }));
  };

  // Render a single proof row. Identical layout for active + completed lists;
  // only the outer wrapper differs (active rows get an orange highlight when
  // they still have unresolved notes). Defined inside the component so it can
  // close over emailLogs / copyShareLink / openMail without prop-drilling.
  const renderProofRow = (p, g, contacts, bucket) => {
    const noteCount = (p.comments || []).length;
    const drawingCount = (p.annotations || []).length;
    const unresolved = proofUnresolvedCount(p);
    const logCount = (emailLogs[p.id]?.length) ?? (p.email_log?.length || 0);
    const needsAttention = unresolved > 0;
    const handleMoveBucket = async () => {
      const target = bucket === "completed" ? "active" : "completed";
      try {
        await setProofCompletion(p, target);
        toast.success(target === "completed" ? "Moved to Completed Proofs" : "Moved back to Active");
      } catch (err) {
        toast.error("Couldn't move proof", { description: err?.response?.data?.detail || err?.message });
      }
    };
    return (
      <li
        key={p.id}
        className={`${p.archived ? "opacity-60" : ""} ${needsAttention ? "bg-amber-50/40 border-l-4 border-l-amber-500" : ""}`}
        data-testid={`proofing-proof-${p.id}`}
        data-needs-attention={needsAttention ? "true" : "false"}
      >
        <div className="px-5 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3 min-w-0 flex-1">
            <FileText className={`w-5 h-5 shrink-0 mt-0.5 ${needsAttention ? "text-amber-600" : "text-zinc-400"}`} />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-zinc-950 truncate">
                {p.name} <span className="text-zinc-400 font-medium">v{p.version}</span>
                {p.archived && <span className="ml-2 text-[10px] text-zinc-500 uppercase tracking-wider">archived</span>}
              </p>
              <p className="text-[11px] text-zinc-500 mt-0.5 inline-flex items-center gap-2 flex-wrap">
                <span>{formatDateUK(p.created_at)}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><MessageSquare className="w-3 h-3" />{noteCount} note{noteCount !== 1 ? "s" : ""}</span>
                <span className="inline-flex items-center gap-1"><Pencil className="w-3 h-3" />{drawingCount} drawing{drawingCount !== 1 ? "s" : ""}</span>
                {unresolved > 0 && <span className="ml-1 inline-flex items-center text-amber-700 font-semibold bg-amber-100 px-1.5 py-0.5 rounded-full">{unresolved} unresolved</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 flex-wrap">
            <StatusBadge status={p.status} />
            <button
              onClick={() => copyShareLink(p, g.project_name)}
              className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-all ${copiedId === p.id ? "bg-emerald-500 text-white" : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"}`}
              data-testid={`proofing-copy-link-${p.id}`}
            >
              {copiedId === p.id ? <><Check className="w-3 h-3" />Copied</> : <><LinkIcon className="w-3 h-3" />Copy link</>}
            </button>
            <RecipientMultiSelectPopover
              contacts={contacts}
              onOpen={() => ensureContacts(g.client_id)}
              onSend={(selected) => openMail(p, g.project_name, selected)}
              triggerTestId={`proofing-email-link-${p.id}`}
              listTestIdPrefix={`proofing-email-recipients-${p.id}`}
            />
            <Link to={`/projects/${p.project_id}?proof=${p.id}&from=proofing`}>
              <Btn size="sm" data-testid={`proofing-open-${p.id}`}>
                <ExternalLink className="w-3.5 h-3.5" />Open
              </Btn>
            </Link>
            <button
              type="button"
              onClick={handleMoveBucket}
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-zinc-100 text-zinc-700 hover:bg-zinc-200 transition-colors"
              title={bucket === "completed" ? "Move this proof back to the Active list" : "Move this proof into the Completed Proofs panel"}
              data-testid={`proofing-toggle-completion-${p.id}`}
            >
              {bucket === "completed"
                ? <><RotateCcw className="w-3 h-3" />Move to Active</>
                : <><Archive className="w-3 h-3" />Move to Completed</>}
            </button>
          </div>
        </div>
        {/* Email history — collapsible audit trail of who got this proof's link */}
        <div className="bg-zinc-50/60 border-t border-zinc-100" data-testid={`proofing-email-history-${p.id}`}>
          <button
            type="button"
            onClick={() => toggleEmailLog(p.id)}
            className="w-full flex items-center justify-between px-5 py-2 hover:bg-zinc-100/60 transition-colors"
            data-testid={`proofing-email-history-toggle-${p.id}`}
          >
            <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1.5">
              <Clock className="w-3 h-3" />
              Email history
              {logCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-bold normal-case tracking-normal">
                  {logCount}
                </span>
              )}
            </span>
            {emailLogOpen[p.id] ? <ChevronUp className="w-3.5 h-3.5 text-zinc-500" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-500" />}
          </button>
          {emailLogOpen[p.id] && (
            <div className="px-5 pb-3 -mt-1" data-testid={`proofing-email-history-list-${p.id}`}>
              {!(p.id in emailLogs) ? (
                <p className="text-xs text-zinc-400 py-2">Loading…</p>
              ) : (emailLogs[p.id] || []).length === 0 ? (
                <p className="text-xs text-zinc-500 italic py-2">No emails sent yet. Click <strong>Email link</strong> above to open a draft for a contact — it'll be logged here.</p>
              ) : (
                <ul className="space-y-1.5">
                  {[...(emailLogs[p.id] || [])].reverse().map((entry, idx) => (
                    <li key={idx} className="flex items-center justify-between gap-3 text-xs bg-white rounded-md px-3 py-1.5 border border-zinc-100" data-testid={`proofing-email-history-entry-${p.id}-${idx}`}>
                      <div className="min-w-0 flex-1">
                        <span className="font-semibold text-zinc-900">{entry.recipient_name || "(unknown)"}</span>
                        {entry.recipient_email && (
                          <span className="text-zinc-500 ml-1.5">&lt;{entry.recipient_email}&gt;</span>
                        )}
                      </div>
                      <span className="text-zinc-500 tabular-nums shrink-0">
                        {new Date(entry.sent_at).toLocaleString("en-GB")}
                      </span>
                    </li>
                  ))}
                  <li className="text-[10px] text-zinc-400 pt-1">
                    Logged when the email draft was opened — mailto can't confirm delivery, so treat as "draft prepared", not "sent &amp; received".
                  </li>
                </ul>
              )}
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <Shell>
      <PageHeader
        testId="proofing-header"
        title="Proofing"
        subtitle={showAll ? "All proofs across every project" : "Proofs from your live projects"}
        actions={
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <Btn
              size="sm"
              variant="primary"
              onClick={() => setVcOpen(true)}
              data-testid="proofing-open-visual-check"
              title="Run an AI visual check on a final artwork PDF"
            >
              <ScanSearch className="w-3.5 h-3.5" />Visual Check
            </Btn>
            <div className="inline-flex items-center border border-zinc-200 rounded-full p-0.5 bg-white">
              <button
                onClick={() => setShowAll(false)}
                data-testid="proofing-filter-live"
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${!showAll ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
              >Live projects only</button>
              <button
                onClick={() => setShowAll(true)}
                data-testid="proofing-filter-all"
                className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${showAll ? "bg-zinc-950 text-white" : "text-zinc-600 hover:text-zinc-900"}`}
              >All</button>
            </div>
          </div>
        }
      />

      {loading ? (
        <div className="flex justify-center py-20"><Spinner /></div>
      ) : grouped.length === 0 ? (
        <Empty
          title={showAll ? "No proofs uploaded yet" : "No proofs on live projects"}
          description={showAll ? "Once you upload a proof on any project, it'll show here." : "Upload a proof on an active project to see it here."}
        />
      ) : (
        <div className="space-y-4">
          {grouped.map((g) => {
            const contacts = contactsByClient[g.client_id] || [];
            return (
            <Card key={g.project_id} className="overflow-hidden border-2 border-purple-100" data-testid={`proofing-project-${g.project_id}`}>
              <div className="bg-gradient-to-br from-purple-50/70 to-white px-5 py-3 border-b border-purple-100 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 grid place-items-center shrink-0">
                    <Briefcase className="w-4 h-4 text-purple-700" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-display text-base font-bold text-zinc-950 truncate">{g.project_name}</p>
                    <p className="text-xs text-zinc-500 truncate">{g.client_name} · #{g.job_number}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={g.project_status} />
                  <Link to={`/projects/${g.project_id}`}>
                    <Btn size="sm" variant="secondary" data-testid={`proofing-open-project-${g.project_id}`}>
                      Open project<ArrowRight className="w-3.5 h-3.5" />
                    </Btn>
                  </Link>
                </div>
              </div>
              {g.active.length > 0 ? (
                <ul className="divide-y divide-zinc-100" data-testid={`proofing-active-list-${g.project_id}`}>
                  {g.active.map((p) => renderProofRow(p, g, contacts, "active"))}
                </ul>
              ) : (
                <div className="px-5 py-4 text-xs text-zinc-500 italic" data-testid={`proofing-no-active-${g.project_id}`}>
                  No active proofs — every proof here is complete. Upload a new version to start the next round.
                </div>
              )}

              {g.completed.length > 0 && (
                <div className="border-t-2 border-dashed border-zinc-200 bg-zinc-50/40" data-testid={`proofing-completed-section-${g.project_id}`}>
                  <button
                    type="button"
                    onClick={() => toggleCompleted(g.project_id)}
                    className="w-full flex items-center justify-between px-5 py-3 hover:bg-zinc-100/60 transition-colors"
                    data-testid={`proofing-completed-toggle-${g.project_id}`}
                  >
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600 inline-flex items-center gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      Completed proofs
                      <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full bg-zinc-200 text-zinc-700 text-[10px] font-bold normal-case tracking-normal">
                        {g.completed.length}
                      </span>
                    </span>
                    {completedOpen[g.project_id]
                      ? <ChevronUp className="w-4 h-4 text-zinc-500" />
                      : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                  </button>
                  {completedOpen[g.project_id] && (
                    <ul className="divide-y divide-zinc-100 border-t border-zinc-200" data-testid={`proofing-completed-list-${g.project_id}`}>
                      {g.completed.map((p) => renderProofRow(p, g, contacts, "completed"))}
                    </ul>
                  )}
                </div>
              )}
            </Card>
            );
          })}
        </div>
      )}

      <VisualCheckDialog open={vcOpen} onClose={() => setVcOpen(false)} />
    </Shell>
  );
};

export default Proofing;
